'use client';

// Bandeja de Segunda Instancia: expedientes en estado SEGUNDA_INSTANCIA (también
// pueden llegar por RECURSO_INTERPUESTO → conceder). Permite "Ver" el detalle y,
// para el Director de segunda instancia, registrar la "Recepción" y "Asignar"
// (reparto a un funcionario de segunda instancia). RBAC por rol de localStorage.

import { useEffect, useState } from 'react';
import { Eye, FileInput, Scale, Send } from 'lucide-react';
import { avisoError, avisoExito } from '@/lib/avisoCentral';
import HeroVista from '@/components/HeroVista';
import { listarExpedientes, type Expediente } from '@/lib/expedientes';
import { api } from '@/lib/api';
import {
  recepcionSegundaInstancia, repartoSegundaInstancia, idSegundaInstancia,
  puedeRepartir2a, usuarioActual,
} from '@/lib/segunda-instancia';
import { getExpediente } from '@/lib/expedientes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

function fmtFecha(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('es-CO');
}

function investigadoPrincipal(e: Expediente): string {
  const inv = e.investigados ?? [];
  if (inv.length === 0) return '—';
  const det = inv.find((x) => x.tipo === 'DETERMINADO' && x.nombre) ?? inv[0];
  return det.nombre || (det.tipo === 'POR_ESTABLECER' ? 'Individualizar' : '—');
}

const hoy = () => new Date().toISOString().slice(0, 10);

interface UsuarioLite { id: string; nombre: string; apellido: string; rol: string; }
const ROLES_FUNCIONARIO_SEGUNDA = ['SEGUNDA_INSTANCIA', 'DIRECTOR_SEGUNDA_INSTANCIA'];

async function funcionariosSegunda(): Promise<UsuarioLite[]> {
  const usuarios = await api<UsuarioLite[]>('/usuarios');
  return usuarios.filter((u) => ROLES_FUNCIONARIO_SEGUNDA.includes(u.rol));
}

export default function BandejaSegundaInstancia({ onAbrir }: { onAbrir?: (id: string) => void }) {
  const [expedientes, setExpedientes] = useState<Expediente[]>([]);
  const [cargando, setCargando] = useState(true);
  const [recepcion, setRecepcion] = useState<Expediente | null>(null);
  const [asignar, setAsignar] = useState<Expediente | null>(null);

  const rol = usuarioActual()?.rol;
  const esDirector = puedeRepartir2a(rol);

  async function cargar() {
    setCargando(true);
    try {
      const res = await listarExpedientes({ estado: 'SEGUNDA_INSTANCIA', limit: 100 });
      setExpedientes(res.data);
    } catch (e) {
      avisoError('No se pudo cargar la bandeja de segunda instancia', e instanceof Error ? e.message : undefined);
    } finally {
      setCargando(false);
    }
  }

  useEffect(() => { cargar(); }, []);

  return (
    <div className="space-y-6">
      <HeroVista
        icono={Scale}
        titulo="Segunda instancia"
        descripcion="Expedientes en segunda instancia. El Director de segunda instancia registra la recepción del expediente remitido y lo reparte a los funcionarios de segunda instancia."
      />

      <Card>
        <CardContent className="pt-6">
          {cargando ? (
            <p className="py-8 text-center text-sm text-muted-foreground">Cargando…</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Radicado</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Investigado</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Funcionario</TableHead>
                  <TableHead className="text-right">Acción</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expedientes.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-medium">
                      {e.radicado}
                      {e.fecha_hechos_tentativa && (
                        <Badge variant="outline" className="ml-2 border-amber-500/50 text-amber-700 dark:text-amber-400" title="Fecha de los hechos tentativa — falta la real">Fecha tentativa</Badge>
                      )}
                    </TableCell>
                    <TableCell>{fmtFecha(e.fecha_queja)}</TableCell>
                    <TableCell>{investigadoPrincipal(e)}</TableCell>
                    <TableCell><Badge variant="secondary">{e.estado_actual}</Badge></TableCell>
                    <TableCell>{e.funcionario_actual || '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex flex-wrap justify-end gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => onAbrir?.(e.id)}>
                          <Eye className="size-3.5" /> Ver
                        </Button>
                        {esDirector && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => setRecepcion(e)}>
                              <FileInput className="size-3.5" /> Recepción
                            </Button>
                            <Button size="sm" onClick={() => setAsignar(e)}>
                              <Send className="size-3.5" /> Asignar
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {expedientes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center text-muted-foreground">
                      No hay expedientes en segunda instancia.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {recepcion && (
        <ModalRecepcion
          expediente={recepcion}
          onClose={() => setRecepcion(null)}
          onListo={(msg) => { setRecepcion(null); avisoExito(msg); cargar(); }}
        />
      )}
      {asignar && (
        <ModalAsignar
          expediente={asignar}
          onClose={() => setAsignar(null)}
          onListo={(msg) => { setAsignar(null); avisoExito(msg); cargar(); }}
        />
      )}
    </div>
  );
}

// ---- Modal: recepción de segunda instancia --------------------------------
function ModalRecepcion({ expediente, onClose, onListo }: {
  expediente: Expediente; onClose: () => void; onListo: (msg: string) => void;
}) {
  const [fecha, setFecha] = useState(hoy());
  const [autoridadRemitente, setAutoridadRemitente] = useState('');
  const [radicadoPrimera, setRadicadoPrimera] = useState('');
  const [constanciaKey, setConstanciaKey] = useState('');
  const [autoConcedeKey, setAutoConcedeKey] = useState('');
  const [recursoKey, setRecursoKey] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!fecha) { avisoError('Indique la fecha de recepción.'); return; }
    setGuardando(true);
    try {
      await recepcionSegundaInstancia(expediente.id, {
        fecha,
        ...(autoridadRemitente.trim() ? { autoridadRemitente: autoridadRemitente.trim() } : {}),
        ...(radicadoPrimera.trim() ? { radicadoPrimera: radicadoPrimera.trim() } : {}),
        ...(constanciaKey.trim() ? { constanciaKey: constanciaKey.trim() } : {}),
        ...(autoConcedeKey.trim() ? { autoConcedeKey: autoConcedeKey.trim() } : {}),
        ...(recursoKey.trim() ? { recursoKey: recursoKey.trim() } : {}),
      });
      onListo(`Recepción registrada para ${expediente.radicado}.`);
    } catch (err) {
      avisoError('No se pudo registrar la recepción', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileInput className="size-5 text-primary" /> Recepción de segunda instancia</DialogTitle>
          <DialogDescription>Radicado: <strong>{expediente.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="fecha">Fecha de recepción</Label>
              <Input id="fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rad">Radicado de primera instancia (opcional)</Label>
              <Input id="rad" value={radicadoPrimera} onChange={(e) => setRadicadoPrimera(e.target.value)} />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="aut">Autoridad remitente (opcional)</Label>
            <Input id="aut" value={autoridadRemitente} onChange={(e) => setAutoridadRemitente(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="ck">Constancia (key)</Label>
              <Input id="ck" value={constanciaKey} onChange={(e) => setConstanciaKey(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ak">Auto concede (key)</Label>
              <Input id="ak" value={autoConcedeKey} onChange={(e) => setAutoConcedeKey(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="rk">Recurso (key)</Label>
              <Input id="rk" value={recursoKey} onChange={(e) => setRecursoKey(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Registrando…' : 'Registrar recepción'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---- Modal: asignar (reparto de segunda instancia) ------------------------
function ModalAsignar({ expediente, onClose, onListo }: {
  expediente: Expediente; onClose: () => void; onListo: (msg: string) => void;
}) {
  const [funcionarios, setFuncionarios] = useState<UsuarioLite[]>([]);
  const [funcionarioId, setFuncionarioId] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [segundaId, setSegundaId] = useState('');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    funcionariosSegunda()
      .then(setFuncionarios)
      .catch((e) => avisoError('No se pudieron cargar los funcionarios', e instanceof Error ? e.message : undefined));
    // Resuelve el id del registro de segunda instancia desde el detalle.
    getExpediente(expediente.id)
      .then((d) => setSegundaId(idSegundaInstancia(d)))
      .catch(() => setSegundaId(''));
  }, [expediente.id]);

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    if (!funcionarioId) { avisoError('Seleccione un funcionario.'); return; }
    if (!segundaId) { avisoError('El expediente aún no tiene recepción de segunda instancia registrada.'); return; }
    setGuardando(true);
    try {
      await repartoSegundaInstancia(segundaId, {
        funcionarioId,
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      });
      onListo(`Expediente ${expediente.radicado} asignado a segunda instancia.`);
    } catch (err) {
      avisoError('No se pudo asignar el expediente', err instanceof Error ? err.message : undefined);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Send className="size-5 text-primary" /> Asignar a segunda instancia</DialogTitle>
          <DialogDescription>Radicado: <strong>{expediente.radicado}</strong></DialogDescription>
        </DialogHeader>
        <form onSubmit={guardar} className="space-y-4">
          <div className="space-y-2">
            <Label>Funcionario de segunda instancia</Label>
            <Select value={funcionarioId} onValueChange={setFuncionarioId}>
              <SelectTrigger className="w-full"><SelectValue placeholder="— Seleccione —" /></SelectTrigger>
              <SelectContent>
                {funcionarios.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.nombre} {f.apellido} · {f.rol}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="obs">Observaciones (opcional)</Label>
            <Textarea id="obs" rows={3} value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancelar</Button>
            <Button type="submit" disabled={guardando}>{guardando ? 'Asignando…' : 'Asignar'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
